import { useEffect, useRef, useState } from 'react';
import {
  NACHWARTEN_MS, OPTIONEN, alsFelder, alsMessung, besser, gutGenug,
  ortungsfehler, standortsatz, type Messung,
} from './standort.ts';
import Kamerafenster, {
  kamerafensterGeht, type Aufnahme,
} from './Kamerafenster.tsx';

/**
 * EIN KNOPF, DER DIE KAMERA ÖFFNET - IN GENAU DIESEM ORDNER.
 *
 * ORIGINALTEXT MARVIN (15.09.2026)
 * > „Des Weiteren will ich das man in jedem Ordner direkt mit einem Knopf
 * >  Bilder machen kann. Die Idee ist die Geschwindigkeit: die Leute sehen von
 * >  der Beschriftung das sie da Bilder machen müssen und können sie aus der
 * >  Plattform machen, ein Klick im richten Ordner, die Kamera öffnet sich,
 * >  Foto aufnehmen und es kommt direkt in den richtige Ordner."
 *
 * ## ER STEHT NEBEN DER ABLAGEFLÄCHE UND NICHT AN IHRER STELLE
 *
 * Die Regel dieses Hauses seit dem 26.08.2026 heisst: EINE Fläche, 2 Gesten,
 * kein Knopf daneben („wenn ich dateien hochladen will, dann überall per drag
 * and drop, nichts anderes"). Hier steht trotzdem ein zweiter Knopf, und der
 * Grund ist, dass er etwas ANDERES tut:
 *
 *   Die Ablagefläche nimmt eine Datei, DIE ES SCHON GIBT.
 *   Dieser Knopf lässt eine ENTSTEHEN.
 *
 * **Ein `capture` an der einen Fläche wäre kein zweiter Weg, sondern der
 * Verlust des ersten:** auf einem Telefon öffnet das Feld dann nur noch die
 * Kamera, und der Lageplan, der schon in der Galerie liegt, ist nicht mehr
 * erreichbar. Deshalb gilt weiter „Kein `capture` am Dateifeld" - dieser
 * Knopf hat sein EIGENES.
 *
 * ## UND DIE MESSUNG LÄUFT, WÄHREND DIE KAMERA OFFEN IST
 *
 * Wo gemessen wird und welche Messung gilt, steht in `standort.ts`; hier
 * steht nur die Verdrahtung. Der Ablauf:
 *
 *   1. Tipp auf den Knopf. `watchPosition` läuft los UND das Dateifeld
 *      öffnet die Kamera - beides im selben Augenblick.
 *   2. Der Monteur fotografiert. Das dauert 5 bis 15 Sekunden, und genau in
 *      dieser Zeit stellt das Gerät von der Funkzelle auf die Satelliten um.
 *   3. Die Datei kommt zurück. Steht eine Messung da, geht das Foto sofort
 *      los; steht keine, wird noch bis zu 4 Sekunden gewartet.
 *   4. Kam gar nichts, wird NICHT hochgeladen. Der Grund steht da, und der
 *      Weg über die Ablagefläche daneben nimmt das Foto ohne Ortsangabe -
 *      **eine fehlende Angabe ist ehrlicher als eine geratene.**
 *
 * ## DIE KAMERA WIRD GEBETEN UND NICHT VERLANGT
 *
 * `capture="environment"` ist ein WUNSCH, kein Riegel: ein Telefon öffnet
 * die rückwärtige Kamera, ein Rechner ohne Kamera zeigt den Dateiwähler.
 * Beides ist richtig. Der Knopf steht deshalb auch am Schreibtisch da und
 * heisst dort, was er tut - er wird nicht nach Bildschirmbreite versteckt:
 * **eine Schwelle, die einen WEG wegnimmt, ist etwas anderes als eine, die
 * eine Aufschrift kürzt.**
 */
export default function Kameraknopf({
  ordner, laeuft, nimm,
}: {
  /** Nur für die Aufschrift der Vorlesehilfe - der Ordner steht am Aufrufer. */
  ordner: string;
  laeuft?: boolean;
  /** Das Foto samt seiner Ortsfelder. `null` heisst: es kam keine Ortsangabe. */
  nimm: (datei: File, felder: Record<string, string>) => void;
}) {
  const feld = useRef<HTMLInputElement>(null);
  const beste = useRef<Messung | null>(null);
  const wache = useRef<number | null>(null);
  const [sucht, setSucht] = useState(false);
  const [anzeige, setAnzeige] = useState<Messung | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);
  const [wartet, setWartet] = useState(false);
  /* DAS AUFGENOMMENE FOTO, SOLANGE ES NOCH KEINEN ORT HAT.
     ========================================================================
     ORIGINALTEXT MARVIN (18.09.2026)
       „du drückst auf den knopf, du kannst das foto aufnehmen am handy, sagst
        „Foto benutzen" aber dann wird es verworfen, es passiert nichts, es
        wird nicht gespeichert."

     HIER STAND `return`. Findet das Gerät seinen Standort nicht, war das Foto
     weg - endgültig, ohne zweiten Versuch, und die Meldung dazu stand oben am
     Seitenanfang, also auf einem Telefon ausserhalb des Bildes. Aus Sicht des
     Monteurs: nichts passiert.

     **Ein Foto, das schon gemacht ist, wird nicht weggeworfen, weil eine
     zweite Angabe fehlt.** Es wartet hier, und der Mensch entscheidet:
     weitersuchen oder ohne Ortsangabe hochladen. Der Riegel „zwingend" bleibt
     damit ganz: ohne Stempel geht es nicht HEIMLICH herein - es geht herein,
     weil jemand es ausdrücklich so will, und dann steht auf dem Bild eben
     nichts. Eine fehlende Angabe ist ehrlicher als eine geratene. */
  const [wartend, setWartend] = useState<File | null>(null);
  /** Wie viele Sekunden noch gesucht wird. Ein Zähler ist eine Auskunft. */
  const [uebrig, setUebrig] = useState(0);
  /* DAS EIGENE KAMERAFENSTER, SOLANGE ES OFFEN IST.
     ========================================================================
     ORIGINALTEXT MARVIN (18.09.2026)
       „foto machen klappt noch immer nicht. das handy nimmt bei der aufnahme
        des bildes doch gps daten. oder du musst, wie das apps machen, die
        amera öffnen aber in einem fenster von uns, wo du dann die ganzen
        georeferenzierten daten direkt einblendest, gps gestartet wird und du
        dann das foto machst. dann brauchst du nicht im nachhinein GPS suchen
        lassen, so wie es jetzt ist funktioniert es nicht."

     ER HAT RECHT, UND ZWAR ÜBER DEN GANZEN WEG. `capture` ruft die Kamera-App
     DES BETRIEBSSYSTEMS; unsere Seite liegt dann im Hintergrund, und dort
     hält das Gerät die Ortungswache an. Gemessen wurde also erst NACH der
     Aufnahme - die .166 hat die Wartezeit von 4 auf 15 Sekunden gestellt und
     damit den VERLUST des Fotos behoben, nicht das Warten. **Eine Wartezeit,
     die man verlängert, bleibt eine Wartezeit.**

     Das eigene Fenster (`teile/Kamerafenster.tsx`) hält die Seite im
     VORDERGRUND: die Messung läuft, während der Monteur ausrichtet, und beim
     Auslösen steht die Zahl schon da. Was hier bleibt, ist der RÜCKFALL -
     ein Browser über `http://`, ein Rechner ohne Kamera, ein Firmenbrowser
     ohne `getUserMedia`. **Ein neuer Weg, der den alten mitnimmt, ist kein
     Fortschritt.** */
  const [fensterOffen, setFensterOffen] = useState(false);

  /* DIE WACHE WIRD IMMER ABGEMELDET. Ein laufendes `watchPosition` hält auf
     einem Telefon das GPS an und leert den Akku - und der Monteur merkt es
     erst am Nachmittag. */
  useEffect(() => () => {
    if (wache.current !== null) navigator.geolocation?.clearWatch(wache.current);
  }, []);

  /* GIBT ES HIER ÜBERHAUPT EINE ORTUNG?
     `navigator.geolocation` steht auch über `http://` da - jeder Aufruf endet
     dann aber mit Code 1, und zwar ohne dass jemand die Ortung abgelehnt
     hätte. Genau das ist der Fall „Tablet im Büro über die LAN-Adresse".
     Gefragt wird deshalb nach dem SICHEREN Zusammenhang und nicht nach dem
     Vorhandensein der Schnittstelle. */
  function ortungMoeglich(): string | null {
    if (typeof window !== 'undefined' && window.isSecureContext === false) {
      return 'Diese Seite ist über eine unverschlüsselte Adresse geöffnet. '
        + 'Ein Browser gibt dort keinen Standort heraus. Rufen Sie dieselbe '
        + 'Adresse mit https:// auf, dann geht es.';
    }
    if (!navigator.geolocation) return ortungsfehler(undefined);
    return null;
  }

  function messenStarten(): boolean {
    const nein = ortungMoeglich();
    if (nein) { setFehler(nein); return false; }
    if (wache.current !== null) return true;   // läuft schon
    setFehler(null);
    setSucht(true);
    wache.current = navigator.geolocation.watchPosition(
      (p) => {
        const m = alsMessung(p as any);
        if (!m) return;
        beste.current = besser(beste.current, m);
        setAnzeige(beste.current);
      },
      (f) => {
        /* EIN FEHLER BEENDET DIE WACHE NICHT. `watchPosition` meldet
           zwischendurch „position unavailable" und findet danach doch noch
           etwas; wer hier abbricht, verliert die Messung, die 2 Sekunden
           später gekommen wäre. Gemeldet wird er nur, wenn bis dahin NICHTS
           dasteht. */
        if (!beste.current) setFehler(ortungsfehler(f?.code));
      },
      OPTIONEN,
    );
    return true;
  }

  function messenBeenden() {
    if (wache.current !== null) {
      navigator.geolocation?.clearWatch(wache.current);
      wache.current = null;
    }
    setSucht(false);
  }

  async function ausgeloest(datei: File) {
    setWartend(null);
    /* STEHT SCHON ETWAS DA, GEHT ES SOFORT LOS. Eine bessere Zahl ist keinen
       Stillstand wert - der Monteur vor der Grube erlebt den als hängende
       Plattform. */
    if (!beste.current) {
      setWartet(true);
      /* EIN ZWEITER, EINMALIGER VERSUCH NACH DER RÜCKKEHR.
         Solange die Kamera offen war, lag die Seite im Hintergrund, und dort
         hält das Gerät die Rückrufe der Wache an. Manche Browser nehmen sie
         danach nicht von selbst wieder auf. `getCurrentPosition` ist die
         Frage, die IMMER beantwortet wird. */
      try {
        navigator.geolocation?.getCurrentPosition(
          (p) => {
            const m = alsMessung(p as any);
            if (m) { beste.current = besser(beste.current, m); setAnzeige(beste.current); }
          },
          () => { /* die Wache läuft weiter, hier ist nichts zu retten */ },
          OPTIONEN,
        );
      } catch { /* ein Browser ohne Ortung; der Fall steht unten */ }

      const bis = Date.now() + NACHWARTEN_MS;
      /* GEWARTET WIRD, BIS ES GUT GENUG IST - nicht bis irgendetwas dasteht.
         Die erste Zahl eines Geräts ist die Funkzelle mit 2000 Metern; wer
         darauf sofort abbricht, brennt sie auf das Nachweisfoto. */
      while (Date.now() < bis
             && !(beste.current && gutGenug(beste.current))) {
        await new Promise((f) => setTimeout(f, 200));
        setUebrig(Math.max(0, Math.ceil((bis - Date.now()) / 1000)));
      }
      setWartet(false);
    }
    messenBeenden();

    const m = beste.current;
    beste.current = null;
    setAnzeige(null);
    if (!m) {
      /* DER GRUND, DEN DIE ORTUNG GEMELDET HAT, SCHLÄGT DEN ALLGEMEINEN SATZ.
         Hier stand `fehler ?? …` - also umgekehrt, und ein alter, harmloser
         Satz aus der Closure überdeckte die wirkliche Ursache. */
      setFehler((fehler ? fehler + ' ' : '')
        + 'Das Gerät hat seinen Standort nicht gefunden. Das Foto ist NICHT '
        + 'verloren: es liegt hier und wartet.');
      setWartend(datei);
      return;
    }
    nimm(datei, alsFelder(m));
  }

  /**
   * WAS AUS DEM EIGENEN FENSTER HERAUSKOMMT.
   *
   * Hier wird NICHT mehr gewartet, und das ist der ganze Gewinn: die Messung
   * lief, während der Monteur ausgerichtet hat. Steht trotzdem keine da
   * (Ortung abgelehnt, Keller, kein Empfang), wartet das Foto wie bisher -
   * **ein Foto, das schon gemacht ist, wird nicht weggeworfen, weil eine
   * zweite Angabe fehlt.**
   */
  function ausDemFenster(a: Aufnahme) {
    setFensterOffen(false);
    if (a.messung) { nimm(a.datei, alsFelder(a.messung)); return; }
    setFehler('Das Gerät hat während der Aufnahme keinen Standort gemeldet. '
      + 'Das Foto ist NICHT verloren: es liegt hier und wartet.');
    setWartend(a.datei);
  }

  /** Das wartende Foto ohne Ortsangabe hochladen - ausdrücklich und von Hand. */
  function ohneOrt() {
    const d = wartend;
    setWartend(null);
    setFehler(null);
    if (d) nimm(d, {});
  }

  /** Noch einmal suchen, mit demselben Foto. */
  function nochmal() {
    const d = wartend;
    if (!d) return;
    setFehler(null);
    if (!messenStarten()) return;
    void ausgeloest(d).catch((f: any) => {
      setFehler(String(f?.message ?? f));
      setWartend(d);
    });
  }

  const beschriftung = laeuft ? 'Lädt hoch'
    : wartet ? `Standort wird gesucht (${uebrig} s)`
    : 'Foto aufnehmen';

  return (
    <div className="kameraknopf">
      {fensterOffen && (
        <Kamerafenster ordner={ordner} fertig={ausDemFenster}
                       zu={() => setFensterOffen(false)}
                       rueckfall={() => {
                         /* DER ALTE WEG, UND ZWAR GENAU DERSELBE.
                            Das Fenster geht zu, die Messung läuft los, das
                            Dateifeld ruft die Kamera-App. Keine zweite
                            Fassung dieser 2 Zeilen - sie stehen oben am
                            Knopf und hier, und beides ist derselbe Ablauf. */
                         setFensterOffen(false);
                         if (!messenStarten()) return;
                         feld.current?.click();
                       }} />
      )}
      <button type="button" className="knopf" disabled={laeuft || wartet}
              aria-label={`Foto aufnehmen für „${ordner}"`}
              onClick={() => {
                /* DER EIGENE WEG ZUERST, UND ZWAR OHNE VORPRÜFUNG DER ORTUNG.
                   Im eigenen Fenster läuft die Messung, WÄHREND ausgerichtet
                   wird, und das Bild gehört uns: es geht auch dann nicht
                   verloren, wenn gar kein Standort kommt. Eine Vorprüfung
                   wäre hier also eine Tür, die vor einem offenen Raum
                   zugeht. */
                if (kamerafensterGeht()) { setFehler(null); setFensterOffen(true); return; }

                /* DER RÜCKFALL AUF DIE KAMERA-APP - MIT Vorprüfung.
                   Hier stand `messenStarten(); feld.current?.click();` ohne
                   Rückgabewert, also auch dann, wenn schon feststand, dass
                   kein Standort kommen kann. Der Monteur fotografierte,
                   drückte „Foto benutzen", und das Foto fiel garantiert
                   durch. **Eine Kamera, die aufgeht, ist ein Versprechen.**
                   BEIDES IM SELBEN AUGENBLICK: erst messen starten, dann die
                   Kamera öffnen - umgekehrt bliebe das Fenster der Kamera
                   zwischen den beiden Zeilen stehen, und die Messung verlöre
                   genau die Sekunden, um derentwillen sie hier steht. */
                if (!messenStarten()) return;
                feld.current?.click();
              }}>
        <Kamerazeichen />
        <span> {beschriftung}</span>
      </button>

      {/* `capture` STEHT HIER UND AN KEINEM ZWEITEN FELD. Es ist ein Wunsch:
          ein Telefon öffnet die Kamera, ein Rechner den Dateiwähler. */}
      <input ref={feld} type="file" accept="image/*" capture="environment"
             style={{ display: 'none' }}
             onChange={(e) => {
               const d = e.target.files?.[0];
               /* DAS FELD WIRD ERST GELEERT, WENN DIE DATEI IN DER HAND IST.
                  Hier stand `e.target.value = ''` VOR dem Weiterreichen; auf
                  iOS ist der Blob einer Kameraaufnahme danach nicht mehr
                  zuverlässig lesbar, und der spätere Versand scheitert still. */
               if (d) {
                 void ausgeloest(d)
                   .catch((f: any) => {
                     /* EIN WEGGEWORFENES VERSPRECHEN IST EINE MELDUNG, DIE
                        NIEMAND SIEHT. Hier stand `void ausgeloest(d)`: eine
                        Ausnahme landete in der Entwicklerkonsole und sonst
                        nirgends. */
                     setFehler('Das Foto ließ sich nicht verarbeiten: '
                               + String(f?.message ?? f));
                     setWartend(d);
                   })
                   .finally(() => { e.target.value = ''; });
               } else {
                 e.target.value = '';
                 /* ABGEBROCHEN ODER LEER ZURÜCKGEKOMMEN - UND BEIDES WIRD
                    GESAGT. Eine Kamera-App, die nach „Foto benutzen" mit
                    leerer Liste zurückkommt (Android bei Speicherdruck, iOS
                    nach einem Neuaufbau der Seite), sah bis hierher genauso
                    aus wie ein Abbruch: nämlich nach gar nichts. */
                 messenBeenden();
                 setFehler('Es kam kein Bild von der Kamera zurück. Wurde die '
                   + 'Aufnahme abgebrochen, ist alles in Ordnung. Sonst noch '
                   + 'einmal auslösen oder das Bild über die Ablagefläche '
                   + 'daneben aus der Galerie hereinziehen.');
               }
             }} />

      {(sucht || anzeige) && !fehler && (
        <span className="klein leise">{standortsatz(anzeige, sucht)}</span>
      )}

      {fehler && (
        <div className="hinweis warnung" style={{ marginTop: 8 }}>
          <b>{wartend ? 'Das Foto wartet' : 'Es kam kein Bild'}</b>
          {fehler}
          {/* DIE ZWEI WEGE STEHEN AM ORT DES KNOPFES UND NICHT OBEN AUF DER
              SEITE. Auf 375 Punkten ist eine Meldung am Seitenanfang, während
              man unten in einer Ordnerkarte steht, keine Meldung. */}
          {wartend && (
            <div className="reihe" style={{ marginTop: 8 }}>
              <button type="button" className="knopf haupt" onClick={nochmal}
                      disabled={wartet}>
                Noch einmal suchen
              </button>
              <button type="button" className="knopf" onClick={ohneOrt}
                      disabled={wartet}>
                Ohne Ortsangabe hochladen
              </button>
              <button type="button" className="knopf" disabled={wartet}
                      onClick={() => { setWartend(null); setFehler(null); }}>
                Verwerfen
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * EINE KAMERA, GEZEICHNET UND NICHT GESCHRIEBEN.
 *
 * Als Schriftzeichen hinge die Gestalt an der Schrift des Geräts, und auf
 * einem Telefon steht dort oft ein buntes Bildchen statt eines Zeichens -
 * dieselbe Lehre wie an den Doppelpfeilen der Bildvorschau.
 */
function Kamerazeichen() {
  return (
    <svg viewBox="0 0 24 18" width="18" height="14" focusable="false"
         aria-hidden fill="none" stroke="currentColor" strokeWidth="1.8"
         strokeLinejoin="round">
      <path d="M1 5.5h4.5L7.5 2.5h9l2 3H23v10H1z" />
      <circle cx="12" cy="10" r="3.6" />
    </svg>
  );
}
